import * as Api from '@ViewModels';
import { Button } from '@WebComponents/Button';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Noop } from '../../../extViewmodels/Utils';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { postNotification } from '../../../models/LocalNotifications';
import { Topics } from '../../../models/LocalNotificationTopics';
import { getDisplayName } from '../../../models/NameUtils';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../models/UiUtils';
import { useAppDrawerContext } from '../../../web/components-v2/Drawer/AppDrawer';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../web/components-v2/Dropdown';
import { ContactAutomationsProgressBanner } from '../../../web/components/contacts/ContactAutomationsProgressBanner';
import { TimelineFilter } from '../../../web/components/entities/EntityTimeline/TimelineFilter';
import { EntityTimelineBare } from '../../../web/components/entities/EntityTimeline';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { DefaultCompactEditorConfig } from '../../../web/components/notes/NoteEditor';
import { RichContentDocumentEditor } from '../../../web/components/richContent/RichContentDocumentEditor';
import { ClockIcon } from '../../../web/components/svgs/icons/ClockIcon';
import { EditItemPenIcon } from '../../../web/components/svgs/icons/EditItemPenIcon';
import { useAudio } from '../../contexts/audioPlayer';
import { useQueueLeadNote } from '../../contexts/queueLeadNote';
import { useTysonMode } from '../../contexts/tysonMode';
import { useWebsockets } from '../../hooks';
import { useQueue } from '../../hooks/queue';
import { LeadViewModel } from '../../viewModels/leads/lead';
import { AidaNoteViewModel } from '../../viewModels/note';
import { IWebsocketSubscription } from '../../viewModels/websockets';
import { AidaActionItem } from '../AidaActionItem';
import { Fab, IFabMenuItem } from '../Fab';
import { HexButton } from '../HexButton';
import { COFFEE_DRAWER_VALUE_DEAL } from '../Queue';
import { BookedGraphic } from '../svgs/BookedGraphic';
import { HookRejectedGraphic } from '../svgs/HookRejectedGraphic';
import { PlayIcon } from '../svgs/icons/audio/PlayIcon';
import { DollarSignIcon } from '../svgs/icons/DollarSignIcon';
import { QualifiedPitchGraphic } from '../svgs/QualifiedPitchGraphic';
import { QuickPitchGraphic } from '../svgs/QuickPitchGraphic';
import { StumpedGraphic } from '../svgs/StumpedGraphic';
import { allEngagementFilterIds, allEventNames, ENGAGEMENT_FILTER_OPTIONS } from './filterOptions';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	lead: LeadViewModel;
	onFabMenuItemClick: (menuItem: IFabMenuItem) => void;
	onFollowUpMenuEditClicked?(followUp: Api.FollowUpEventViewModel): void;
}

// Stays at .v2 even though the file-events category that prompted the bump is gone: reverting to the v1 key
// would resurrect stale v1 selections. Values holding file types self-heal, since reads drop unknown types.
const typesStorageKey = 'aidaTimelineFilterOptions.v2';

const fabMenuItems: IFabMenuItem[] = [
	{
		dataContext: 'CreateDeal',
		icon: <DollarSignIcon style={{ color: 'white' }} />,
		tooltip: 'Create a Deal',
	},
	{
		dataContext: 'UpdateDeal',
		icon: <DollarSignIcon style={{ color: 'white' }} />,
		tooltip: 'Update Deal',
	},
	{
		dataContext: 'AddNote',
		icon: <EditItemPenIcon fillColor='#fff' />,
		tooltip: 'Add Note',
	},
	{
		dataContext: 'SetFollowUp',
		icon: <ClockIcon fillColor='#fff' />,
		tooltip: 'Set Follow Up',
	},
];

const supportedActions: Api.TimelineEventTypes[] = [
	'SentEmailEvent',
	'NoteEvent',
	'PhoneCallEvent',
	'AutomationEvent',
	'AutomationCompletedEvent',
	'ReceivedEmailEvent',
	'ViewedEmailEvent',
	'RepliedEmailEvent',
	'ContactArchivedEvent',
	'DealCreatedEvent',
	'DealUpdatedEvent',
	'PhoneCallCompletedEvent',
	'UntrackedPhoneCallEvent',
	'FollowUpEvent',
	'CancelledFollowUpEvent',
	'RescheduledFollowUpEvent',
	// - Removed to avoid bias for subsequent serving 'SkipLeadEvent',
];

const renderContentFor: Api.TimelineEventTypes[] = ['ActionItemEvent', 'PhoneCallCompletedEvent'];

const knownEngagementTypes = new Set<string>(allEventNames);

// Splits on ',' as well as '|': the previous writer joined with '|' then ran a single `.replace(',', '|')`,
// which only converts the FIRST comma, so stored values can still hold comma-joined type groups. Anything
// unrecognized is dropped — that covers the 'All' sentinel and the texts/action items this view never showed.
const readStoredTypes = () => {
	try {
		return window.localStorage
			.getItem(typesStorageKey)
			?.split(/[|,]/)
			.filter(type => knownEngagementTypes.has(type));
	} catch {
		return undefined;
	}
};

const loadRichContentSupportedTypesFromLocalStorage = (): Api.TimelineEventTypes[] => {
	const storedTypes = readStoredTypes();
	return storedTypes?.length ? (storedTypes as Api.TimelineEventTypes[]) : allEventNames;
};

const getSelectedEngagementFilterIds = (): string[] => {
	const storedTypes = readStoredTypes();
	if (!storedTypes?.length) {
		return allEngagementFilterIds;
	}
	const selectedIds = ENGAGEMENT_FILTER_OPTIONS.filter(option =>
		option.types.some(type => storedTypes.includes(type))
	).map(option => option.id);
	return selectedIds.length > 0 ? selectedIds : allEngagementFilterIds;
};

const TimelineBase: React.FC<IProps> = ({ className = '', onFabMenuItemClick, onFollowUpMenuEditClicked }) => {
	const userSession = useUserSession();
	const queue = useQueue();
	const tysonMode = useTysonMode();
	const websockets = useWebsockets();
	const websocketSubscriptionName = useRef(`timeline-${queue.lead?.company?.id}`).current;
	const { setDefaultNote } = useQueueLeadNote();
	const timeline = useRef(new Api.EntityTimelineViewModel(queue.lead?.company)).current;
	const [hasFetchedFirstPage, setHasFetchedFirstPage] = useState(false);
	const [timelineEventTypes, setTimelineEventTypes] = useState<Api.TimelineEventTypes[]>(
		loadRichContentSupportedTypesFromLocalStorage
	);
	const [engagementFilterIds, setEngagementFilterIds] = useState<string[]>(getSelectedEngagementFilterIds);
	const [currentFabMenuItems, setCurrentFabMenuItems] = useState(fabMenuItems);
	const audioContext = useAudio();

	const appDrawerContext = useAppDrawerContext();

	const onEngagementFilterCommit = (selectedIds: string[]) => {
		const types = ENGAGEMENT_FILTER_OPTIONS.filter(option => selectedIds.includes(option.id)).flatMap(
			option => option.types
		) as Api.TimelineEventTypes[];
		try {
			window.localStorage.setItem(typesStorageKey, types.join('|'));
		} catch {
			// Storage unavailable (private mode / quota) — the filter still applies for this session.
		}
		setEngagementFilterIds(selectedIds);
		setTimelineEventTypes(types.length > 0 ? types : allEventNames);
	};

	useEffect(() => {
		// fetch 1 item (including all types) for considering last interaction

		timeline.controller.getNext(null, 1, { typeOf: allEventNames }).then(() => setHasFetchedFirstPage(true));

		// subscribe to websockets for timeline updates
		const subscription: IWebsocketSubscription = {
			callback: () => {
				postNotification({ topic: Topics.TIMELINE_REFRESH });
			},
			events: ['TimelineEvent'],
			name: websocketSubscriptionName,
		};

		websockets.subscribe(subscription);

		return () => websockets.unsubscribe(websocketSubscriptionName);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (queue.lead?.lastDealIsOpen) {
			setCurrentFabMenuItems(fabMenuItems.filter(x => x.dataContext !== 'CreateDeal'));
		} else {
			setCurrentFabMenuItems(fabMenuItems.filter(x => x.dataContext !== 'UpdateDeal'));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queue.companyId]);

	useEffect(() => {
		setHasFetchedFirstPage(queue.lead?.company.richContentViewModel?.controller?.hasFetchedFirstPage);
	}, [queue.lead?.company.richContentViewModel?.controller?.hasFetchedFirstPage]);

	const lastInteraction = useMemo(() => {
		const timelineList = timeline.controller?.fetchResults?.toArray();

		if (!timelineList?.length) {
			return 'None';
		}

		const recent = moment(timelineList[0].timestamp);
		const now = moment();

		const diffMins = now.diff(recent, 'minutes');
		if (diffMins < 5) {
			return 'Just now';
		}

		let diff = now.diff(recent, 'hours');
		if (diff < 1) {
			return 'Less than an hour ago';
		} else if (diff < 24) {
			return `${diff} hour${diff > 1 ? 's' : ''} ago`;
		}

		diff = now.diff(recent, 'days');
		if (diff < 7) {
			return `${diff} days ago`;
		}

		diff = now.diff(recent, 'weeks');
		if (diff < 4) {
			return `${diff} week${diff > 1 ? 's' : ''} ago`;
		}

		diff = now.diff(recent, 'months');
		if (diff < 12) {
			return `${diff} month${diff > 1 ? 's' : ''} ago`;
		}

		diff = now.diff(recent, 'years');
		return `About ${diff} year${diff > 1 ? 's' : ''} ago`;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasFetchedFirstPage]);

	const onFollowUpMenuItemClicked = (followUp: Api.FollowUpEventViewModel) => (menuItem: 'edit' | 'delete') => {
		if (menuItem === 'edit') {
			onFollowUpMenuEditClicked(followUp);
		} else if (menuItem === 'delete') {
			queue
				.deleteFollowUp()
				.then(() => toast.success('Follow up was removed'))
				.catch((err: Api.IOperationResultNoValue) => toast.error(`Unable to remove follow up: ${err.systemMessage}`));
		}
	};

	const handleEditNote = (richContent: Api.RichContentViewModel) => {
		if (richContent instanceof Api.NoteViewModel) {
			setDefaultNote(new AidaNoteViewModel(userSession, richContent.toJs()));
		}
	};

	const renderAction = (event: Api.TimelineEventViewModel<Api.ITimelineEvent>) => {
		const dealHasSameId = queue.lead?.lastDeal?.id === event.resourceId;
		const dealIdExists = queue.lead?.lastDeal?.id !== null;
		const isDealEvent = event.type === 'DealUpdatedEvent' || event.type === 'DealCreatedEvent';

		if (isDealEvent && dealHasSameId && dealIdExists) {
			const onClick = () => {
				queue.lead?.loadDealForm(queue.lead?.lastDeal?.id);
				appDrawerContext.setValue(COFFEE_DRAWER_VALUE_DEAL);
			};
			return <Button label='Edit Deal' size='small' onClick={onClick} />;
		}

		if (event.type === 'NoteEvent') {
			const richContent = (event as Api.NoteEventViewModel).viewmodel;
			if (richContent.canEdit) {
				return (
					<Dropdown>
						<DropdownEllipsisTrigger />
						<DropdownContent>
							<DropdownItem onClick={() => handleEditNote(richContent)}>Edit</DropdownItem>
						</DropdownContent>
					</Dropdown>
				);
			}
		}

		if (event.type.includes('FollowUp')) {
			const followUp = event as Api.FollowUpEventViewModel;
			if (event.type !== 'CancelledFollowUpEvent') {
				return (
					<Dropdown>
						<DropdownEllipsisTrigger />
						<DropdownContent>
							<DropdownItem onClick={() => onFollowUpMenuItemClicked(followUp)('edit')}>Edit follow up</DropdownItem>
							<DropdownItem onClick={() => onFollowUpMenuItemClicked(followUp)('delete')}>
								Delete follow up
							</DropdownItem>
						</DropdownContent>
					</Dropdown>
				);
			}
		}

		return null;
	};

	const renderContent = useCallback((event: Api.TimelineEventViewModel<Api.ITimelineEvent>) => {
		switch (event.type) {
			case 'ActionItemEvent': {
				return <AidaActionItem actionItem={(event as Api.ActionItemEventViewModel).viewmodel} />;
			}
			case 'PhoneCallCompletedEvent': {
				const phoneCallEvent = event as Api.PhoneCallCompletedEventViewModel;
				const phoneCall = phoneCallEvent.phoneCall;

				const onClick = () => {
					audioContext.setIsOpen(true)();

					audioContext.setAudioSrc(phoneCall.recording?.publicUrl);

					audioContext.setTitle(`Call Recording (${moment(phoneCall.creationDate).format('M/DD/YYYY [at] h:mma')})`);

					audioContext.setSubtitle(`${getDisplayName(phoneCall.creator)} called ${phoneCall.phoneNumber?.standard}`);

					audioContext.setPhoneCallId(phoneCall.id);

					audioContext.setSurface('timeline');
				};
				return (
					<>
						{phoneCallEvent.viewmodel.rawContentState && (
							<RichContentDocumentEditor
								autoFocus={false}
								className='note-editor-body-editor'
								config={DefaultCompactEditorConfig}
								contentState={convertRawRichTextContentStateToRichContentEditorState(
									phoneCallEvent.viewmodel.rawContentState
								)}
								readOnly={true}
							/>
						)}
						{!!phoneCall?.recording?.publicUrl && (
							<div className={css(styleSheet.recordingLink)} onClick={onClick}>
								<PlayIcon className={css(styleSheet.playIcon)} />
								Play Recording
							</div>
						)}
					</>
				);
			}
			default: {
				break;
			}
		}

		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const callIcon = useMemo(() => {
		const type = timeline.controller.fetchResults
			.reverse()
			.find(
				x => x.type === 'PhoneCallCompletedEvent' || x.type === 'DealCreatedEvent' || x.type === 'DealUpdatedEvent'
			);
		let graphic = null;
		if (!type) {
			return graphic;
		}

		const kind = type.title.match(/\([\w\s!]*\)\s*$/)?.[0];
		switch (kind) {
			case '(Demo Booked!)':
			case '(Demo Rescheduled)':
				graphic = <BookedGraphic />;
				break;
			case '(Quick Pitch)':
				graphic = <QuickPitchGraphic />;
				break;
			case '(Qualified Pitch)':
				graphic = <QualifiedPitchGraphic />;
				break;
			case '(Stumped By Objection)':
				graphic = <StumpedGraphic />;
				break;
			case '(Hook Rejected)':
				graphic = <HookRejectedGraphic />;
				break;
			default:
				return null;
		}
		return {
			display: <HexButton className={css(styleSheet.hexButton)} graphic={graphic} onClick={Noop} text='' />,
			text: kind.replace('(', '').replace(')', '').replace('!', ''),
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [timeline?.controller?.fetchResults?.length]);

	const contacts = queue.lead.contacts;

	return (
		<div className={`${css(styleSheet.timelineContainer)} ${className}`}>
			{contacts &&
				contacts?.map(contact => {
					if (contact?.inProgressAutomations?.length <= 0) {
						return null;
					}
					return (
						<div className={css(styleSheet.automationBanner)} key={contact.id}>
							<ContactAutomationsProgressBanner
								contact={contact}
								customTitle={`Automation in Progress for ${contact.firstName}`}
							/>
						</div>
					);
				})}

			<div className={css(styleSheet.timelineHeader)}>
				<div className={css(styleSheet.lastInteractionContainer)}>
					<span className={css(styleSheet.lastContact)}>
						Last
						<br />
						Contact
					</span>
					<span>{callIcon?.display}</span>
					<div>
						<div>{lastInteraction}</div>
						<div className={css(styleSheet.lastInteractionTime)}>{callIcon?.text}</div>
					</div>
				</div>
				<div className={css(styleSheet.engagementsTypesSelectContainer)}>
					<TimelineFilter
						options={ENGAGEMENT_FILTER_OPTIONS}
						selectedIds={engagementFilterIds}
						onCommit={onEngagementFilterCommit}
						styles={[styleSheet.engagementsTypesSelect]}
					/>
				</div>
				{!tysonMode.isActive && (
					<Fab className={css(styleSheet.fab)} menuItems={currentFabMenuItems} onMenuItemClick={onFabMenuItemClick} />
				)}
			</div>
			{!queue.lead ? (
				<LoadingSpinner className={css(styleSheet.loadingSpinner)} type='large' />
			) : (
				<EntityTimelineBare
					className={css(styleSheet.timeline)}
					entity={queue.lead.company}
					idsToExclude={[queue.lead.executiveSummary.id]}
					renderAction={renderAction}
					renderActionFor={supportedActions}
					renderContent={renderContent}
					renderContentFor={renderContentFor}
					placeholderText={"You haven't had any interactions yet"}
					eventTypes={timelineEventTypes}
					userSession={userSession}
				/>
			)}
		</div>
	);
};

export const Timeline = observer(TimelineBase);
